/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * v2 shell state remembered per user (sidebar collapsed, collapsed sections,
 * favourites, active app), stored server-side in ui_prefs via PATCH /users/me/ui-preferences
 * so it follows the user to another browser. Updates are optimistic: the UI
 * moves at once and the write happens behind it; a failed write is logged and
 * the local value stays for this session.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import useUiVersion from "hooks/useUiVersion";

const MAX_FAVOURITES = 10;

export default function useShellPrefs() {
  const { uiPrefs, ready, setUiPref } = useUiVersion();
  const [local, setLocal] = useState({});

  // Server values arrive after the first render; adopt them for keys the user
  // has not changed in this session.
  useEffect(() => {
    if (ready) setLocal((prev) => ({ ...uiPrefs, ...prev }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  const persist = useCallback(
    (key, value) => {
      setLocal((prev) => ({ ...prev, [key]: value }));
      setUiPref(key, value).catch((error) => {
        // eslint-disable-next-line no-console
        console.warn(`Could not save ${key}`, error);
      });
    },
    [setUiPref]
  );

  const collapsed = Boolean(local.sidebarCollapsed);
  const collapsedSections = useMemo(
    () => (Array.isArray(local.collapsedSections) ? local.collapsedSections : []),
    [local.collapsedSections]
  );
  const favourites = useMemo(() => (Array.isArray(local.favourites) ? local.favourites : []), [local.favourites]);

  const setCollapsed = useCallback((value) => persist("sidebarCollapsed", value), [persist]);
  const activeApp = typeof local.activeApp === "string" ? local.activeApp : null;
  const setActiveApp = useCallback((code) => persist("activeApp", code), [persist]);

  const toggleSection = useCallback(
    (sectionKey) => {
      const next = collapsedSections.includes(sectionKey)
        ? collapsedSections.filter((k) => k !== sectionKey)
        : [...collapsedSections, sectionKey];
      persist("collapsedSections", next);
    },
    [collapsedSections, persist]
  );

  const toggleFavourite = useCallback(
    (routeKey) => {
      const next = favourites.includes(routeKey)
        ? favourites.filter((k) => k !== routeKey)
        : [...favourites, routeKey].slice(-MAX_FAVOURITES);
      persist("favourites", next);
    },
    [favourites, persist]
  );

  return { collapsed, setCollapsed, collapsedSections, toggleSection, favourites, toggleFavourite, activeApp, setActiveApp };
}
